import type {
  ToolCalibrationDetailPublic,
  ToolDetailPublic,
  ToolLoanDetailPublic,
} from "@common/client"
import { z } from "zod"

export const SECTIONS = [
  "details",
  "calibration",
  "loans",
  "attachments",
] as const

export type Section = (typeof SECTIONS)[number]

export const paneSearchSchema = z.object({
  section: z.enum(SECTIONS).optional().catch(undefined),
})

export function sectionTitle(section: Section): string | undefined {
  switch (section) {
    case "details":
      return undefined
    case "calibration":
      return "Calibration"
    case "loans":
      return "Loans"
    case "attachments":
      return "Attachments"
  }
}

export type Attachment = {
  key: string
  documentId: string
  name: string
  caption: string | null
  source: string
  addedBy: string | null
  addedAt: string
  removableId: string | null
}

export function attachmentsOf(tool: ToolDetailPublic): Attachment[] {
  const certificates = tool.active_calibrations.flatMap((calibration) =>
    calibration.certificate_document && calibration.performed_on
      ? [
          {
            key: `calibration-${calibration.id}`,
            documentId: calibration.certificate_document.id,
            name: calibration.certificate_document.file_name,
            caption: calibration.certificate_number,
            source: "Calibration",
            addedBy: calibration.performed_by,
            addedAt: calibration.performed_on,
            removableId: null,
          },
        ]
      : [],
  )
  const receipts = tool.active_loans.flatMap((loan) =>
    loan.return_document && loan.returned_at
      ? [
          {
            key: `loan-${loan.id}`,
            documentId: loan.return_document.id,
            name: loan.return_document.file_name,
            caption: loan.return_note,
            source: `Receipt · ${loan.work_package?.number ?? "Work order"}`,
            addedBy: loan.received_by
              ? `${loan.received_by.first_name} ${loan.received_by.last_name}`
              : null,
            addedAt: loan.returned_at,
            removableId: null,
          },
        ]
      : [],
  )
  const manual = tool.active_documents.flatMap((attachment) =>
    attachment.document
      ? [
          {
            key: `document-${attachment.id}`,
            documentId: attachment.document.id,
            name: attachment.document.file_name,
            caption: attachment.caption,
            source: "Manual",
            addedBy: `${attachment.added_by.first_name} ${attachment.added_by.last_name}`,
            addedAt: attachment.created_at,
            removableId: attachment.id,
          },
        ]
      : [],
  )
  return [...certificates, ...receipts, ...manual].sort((left, right) =>
    right.addedAt.localeCompare(left.addedAt),
  )
}

export function openLoan(tool: ToolDetailPublic): ToolLoanDetailPublic | null {
  return tool.active_loans.find((loan) => loan.returned_at === null) ?? null
}

export function openCalibration(
  tool: ToolDetailPublic,
): ToolCalibrationDetailPublic | null {
  return (
    tool.active_calibrations.find(
      (calibration) => calibration.result === null,
    ) ?? null
  )
}
